import { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Button,
  SelectChangeEvent,
  Checkbox,
} from '@mui/material';
import {
  BLType,
  InventoryType,
  ProductImage,
  ProductVariant,
} from './inventory.types';
import { useAuth } from '../../auth/useAuth';
import axios from 'axios';
import base64ToBlob from '../../utils/base64ToBlob';
import { API_URL } from '../../config/config';
import { useNavigate } from 'react-router-dom';

const CreateInventory = () => {
  const [formData, setFormData] = useState<InventoryType>({
    name: '',
    quantity: 0,
    status: 'active',
  });
  const { user } = useAuth();
  const [stores, setStores] = useState<BLType[]>([]);
  const [warehouses, setWarehouses] = useState<BLType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [locationType, setLocationType] = useState<'store' | 'warehouse'>(
    'store',
  );
  const [variants, setVariants] = useState<ProductVariant[]>([
    {
      name: '',
      size: '',
      color: '',
      material: '',
      sku: '',
      barcode: '',
    },
  ]);
  const [productImages, setProductImages] = useState<ProductImage[]>([]);
  const [postToIg, setPostToIg] = useState<boolean | null>(true);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const navigate = useNavigate();
  useEffect(() => {
    const getStores = async () => {
      try {
        setLoading(true);
        if (!user) return null;
        const res = await fetch(`${API_URL}/store/${user.id}/`);
        const resWH = await await fetch(`${API_URL}/warehouse/${user.id}/`);
        if (!res.ok || !resWH.ok) {
          const text = await res.text();
          throw new Error(text || 'Failed to fetch stores or stores.');
        }

        const data = await res.json();
        const dataWH = await resWH.json();
        setStores(data.data);
        setWarehouses(dataWH.data);
      } catch (err: any) {
        setError(err.message || 'Unexpected error');
      } finally {
        setLoading(false);
      }
    };

    getStores();
  }, [user]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;

    // List of product fields (based on your Product interface)
    const productFields = [
      'sku',
      'barcode',
      'brand',
      'category',
      'description',
      'default_price',
      'default_cost',
      'type',
      'tags',
      'dimensions',
      'weight',
      'min_order_quantity',
      'country_of_origin',
      'hsn_code',
      'compatibility',
      'warranty_period',
      'hazardous',
      'is_bundle',
      'tax_category',
    ];

    if (productFields.includes(name)) {
      setFormData((prev: any) => ({
        ...prev,
        product: {
          ...(prev.product ?? {}),
          [name]: value,
        },
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleVariantChange = (
    index: number,
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setVariants(prev => {
      const updated = [...prev];
      updated[index][name as keyof ProductVariant] = value;
      return updated;
    });
  };

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;

    const selectedLocation =
      locationType === 'store'
        ? stores.find(loc => String(loc.id) === value)
        : warehouses.find(loc => String(loc.id) === value);

    if (selectedLocation?.ig_user_id) {
      setIsConnected(true);
    }

    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePostToIg = () => {
    setPostToIg(prev => {
      const newValue = !prev;
      return newValue;
    });
  };

  const addVariant = () => {
    setVariants(prev => [
      ...prev,
      {
        name: '',
        size: '',
        color: '',
        material: '',
        sku: '',
        barcode: '',
        id: '',
        product_id: '',
      },
    ]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData || !user) return;

    try {
      setLoading(true);

      const fd = new FormData();

      fd.append('postToIG', String(formData.postToIg ?? ''));

      // 1. Append the actual image files
      productImages.forEach((img, index) => {
        const blob = base64ToBlob(img.url); // convert base64 → Blob
        fd.append('files', blob, `image_${index}.jpg`);
      });

      // 2. Append only metadata, no base64
      const metadata = productImages.map((img, index) => ({
        order: index,
        caption: img.alt_text || '',
      }));
      fd.append('metadata', JSON.stringify(metadata));

      // Append inventory data
      fd.append(
        'store_id',
        locationType === 'store' ? (formData.store_id ?? '') : '',
      );
      fd.append(
        'warehouse_id',
        locationType === 'warehouse' ? (formData.warehouse_id ?? '') : '',
      );

      fd.append('quantity', String(formData.quantity));

      // Flatten and append product fields
      Object.entries(formData.product || {}).forEach(([key, value]) => {
        fd.append(`product[${key}]`, value as any);
      });

      fd.append('name', formData.name ?? '');

      // Append variants
      variants.forEach((variant, index) => {
        Object.entries(variant).forEach(([key, value]) => {
          fd.append(`variants[${index}][${key}]`, value as any);
        });
      });

      const endpoint =
        locationType === 'store'
          ? `${API_URL}/inventory/${user.id}/store`
          : `${API_URL}/inventory/${user.id}/warehouse`;

      const res = await axios.post(endpoint, fd, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (res.status === 200) {
        alert('Inventory added successfully!');
      }
      navigate('/warehouses');
    } catch (err) {
      console.error('Error submitting inventory:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        maxWidth: 600,
        mx: 'auto',
        mt: 4,
      }}
    >
      <Typography variant="h5">Add Stock</Typography>

      {/* Inventory Fields */}
      <TextField
        label="Batch Name"
        name="batch_number"
        onChange={handleChange}
        fullWidth
      />
      <TextField
        label="Quantity"
        name="quantity"
        type="number"
        onChange={handleChange}
        fullWidth
      />
      <TextField
        label="Reorder Level"
        name="reorder_level"
        type="number"
        onChange={handleChange}
        fullWidth
      />
      <TextField
        label="Price Override"
        name="price_override"
        type="number"
        onChange={handleChange}
        fullWidth
      />
      <TextField
        label="Expiry Date"
        name="expiry_date"
        type="date"
        onChange={handleChange}
        fullWidth
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        label="Location in Store"
        name="location_in_store"
        onChange={handleChange}
        fullWidth
      />

      <FormControl fullWidth>
        <InputLabel>Status</InputLabel>
        <Select
          name="status"
          value={formData.status ?? ''}
          onChange={handleSelectChange}
        >
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="discontinued">Discontinued</MenuItem>
          <MenuItem value="out_of_stock">Out of Stock</MenuItem>
        </Select>
      </FormControl>

      {/* Location Selection */}
      <FormControl>
        <Typography>Select Location Type:</Typography>
        <RadioGroup
          row
          value={locationType}
          onChange={e =>
            setLocationType(e.target.value as 'store' | 'warehouse')
          }
        >
          <FormControlLabel value="store" control={<Radio />} label="Store" />
          <FormControlLabel
            value="warehouse"
            control={<Radio />}
            label="Warehouse"
          />
        </RadioGroup>
      </FormControl>

      <FormControl fullWidth>
        <InputLabel>
          {locationType === 'store' ? 'Store' : 'Warehouse'}
        </InputLabel>
        <Select
          name={locationType === 'store' ? 'store_id' : 'warehouse_id'}
          value={
            locationType === 'store'
              ? (formData.store_id ?? '')
              : (formData.warehouse_id ?? '')
          }
          onChange={handleSelectChange}
        >
          {(locationType === 'store' ? stores : warehouses).map(loc => (
            <MenuItem key={loc.id} value={loc.id}>
              {loc.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Typography variant="h5">Product Fields</Typography>

      {/* Product Fields */}
      <TextField
        label="Name"
        name="name"
        value={formData.name}
        onChange={handleChange}
        fullWidth
      />
      <TextField
        label="SKU"
        name="sku"
        value={formData?.product?.sku ?? ''}
        onChange={handleChange}
        fullWidth
      />
      <TextField
        label="Barcode"
        name="barcode"
        value={formData?.product?.barcode ?? ''}
        onChange={handleChange}
        fullWidth
      />
      <TextField
        label="Brand"
        name="brand"
        value={formData?.product?.brand ?? ''}
        onChange={handleChange}
        fullWidth
      />
      <TextField
        label="Category"
        name="category"
        value={formData?.product?.category ?? ''}
        onChange={handleChange}
        fullWidth
      />
      <TextField
        label="Description"
        name="description"
        value={formData?.product?.description ?? ''}
        onChange={handleChange}
        multiline
        rows={2}
        fullWidth
      />
      <TextField
        label="Default Price"
        name="default_price"
        type="number"
        value={formData?.product?.default_price ?? ''}
        onChange={handleChange}
        fullWidth
      />
      <TextField
        label="Default Cost"
        name="default_cost"
        type="number"
        value={formData?.product?.default_cost ?? ''}
        onChange={handleChange}
        fullWidth
      />
      <FormControl fullWidth>
        <InputLabel>Type</InputLabel>
        <Select
          name="type"
          value={formData?.product?.type ?? ''}
          onChange={e =>
            setFormData((prev: any) => ({
              ...prev,
              product: {
                ...(prev.product ?? {}),
                type: e.target.value, // keep as string
              },
            }))
          }
        >
          <MenuItem value="physical">Physical</MenuItem>
          <MenuItem value="digital">Digital</MenuItem>
          <MenuItem value="service">Service</MenuItem>
        </Select>
      </FormControl>

      <TextField
        label="Tags (comma separated)"
        name="tags"
        value={formData?.product?.tags ?? ''}
        onChange={handleChange}
        fullWidth
      />
      <TextField
        label="Dimensions"
        name="dimensions"
        value={formData?.product?.dimensions ?? ''}
        onChange={handleChange}
        fullWidth
      />
      <TextField
        label="Weight (kg)"
        name="weight"
        type="number"
        value={formData?.product?.weight ?? ''}
        onChange={handleChange}
        fullWidth
      />
      <TextField
        label="Min Order Quantity"
        name="min_order_quantity"
        type="number"
        value={formData?.product?.min_order_quantity ?? ''}
        onChange={handleChange}
        fullWidth
      />
      <TextField
        label="Country of Origin"
        name="country_of_origin"
        value={formData?.product?.country_of_origin ?? ''}
        onChange={handleChange}
        fullWidth
      />
      <TextField
        label="HSN Code"
        name="hsn_code"
        value={formData?.product?.hsn_code ?? ''}
        onChange={handleChange}
        fullWidth
      />
      <TextField
        label="Compatibility"
        name="compatibility"
        value={formData?.product?.compatibility ?? ''}
        onChange={handleChange}
        fullWidth
      />
      <TextField
        label="Warranty Period"
        name="warranty_period"
        value={formData?.product?.warranty_period ?? ''}
        onChange={handleChange}
        fullWidth
      />

      <FormControl fullWidth>
        <InputLabel>Hazardous</InputLabel>
        <Select
          name="hazardous"
          value={formData?.product?.hazardous?.toString() ?? ''}
          onChange={e =>
            setFormData((prev: any) => ({
              ...prev,
              product: {
                ...(prev.product ?? {}),
                hazardous: e.target.value === 'true',
              },
            }))
          }
        >
          <MenuItem value="true">Yes</MenuItem>
          <MenuItem value="false">No</MenuItem>
        </Select>
      </FormControl>

      <FormControl fullWidth>
        <InputLabel>Is Bundle</InputLabel>
        <Select
          name="is_bundle"
          value={formData?.product?.is_bundle?.toString() ?? ''}
          onChange={e =>
            setFormData((prev: any) => ({
              ...prev,
              product: {
                ...(prev.product ?? {}),
                is_bundle: e.target.value === 'true',
              },
            }))
          }
        >
          <MenuItem value="true">Yes</MenuItem>
          <MenuItem value="false">No</MenuItem>
        </Select>
      </FormControl>

      <TextField
        label="Tax Category"
        name="tax_category"
        value={formData?.product?.tax_category ?? ''}
        onChange={handleChange}
        fullWidth
      />

      {/* Product Variant Fields */}
      {variants.map((variant, index) => (
        <Box key={index} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Size"
            name="size"
            value={variant.size ?? ''}
            onChange={e => handleVariantChange(index, e)}
          />
          <TextField
            label="Color"
            name="color"
            value={variant.color ?? ''}
            onChange={e => handleVariantChange(index, e)}
          />
          <TextField
            label="Material"
            name="material"
            value={variant.material ?? ''}
            onChange={e => handleVariantChange(index, e)}
          />
          <TextField
            label="SKU"
            name="sku"
            value={variant.sku ?? ''}
            onChange={e => handleVariantChange(index, e)}
          />
          <TextField
            label="Barcode"
            name="barcode"
            value={variant.barcode ?? ''}
            onChange={e => handleVariantChange(index, e)}
          />
        </Box>
      ))}

      {/* Add another variant */}
      <Button variant="outlined" onClick={addVariant}>
        + Add Another Variant
      </Button>

      <Box>
        <Typography variant="h6" gutterBottom>
          Product Images
        </Typography>

        <Button variant="outlined" component="label">
          Upload Images
          <input
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={e => {
              const files = e.target.files;
              if (!files) return;

              // const imagePreviews: ProductImage[] = [];

              Array.from(files).forEach(file => {
                const reader = new FileReader();
                reader.onloadend = () => {
                  setProductImages(prev => [
                    ...prev,
                    {
                      id: crypto.randomUUID(), // Temporary ID
                      product_id: formData?.product?.id ?? '', // Optional
                      url: reader.result as string,
                      alt_text: '',
                      created_at: new Date().toISOString(),
                      updated_at: new Date().toISOString(),
                    },
                  ]);
                };
                reader.readAsDataURL(file);
              });

              // Reset file input (can re-upload same file again)
              e.target.value = '';
            }}
          />
        </Button>

        {/* Image Preview List */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
          {productImages.map((img, idx) => (
            <Box key={img.id} sx={{ border: '1px solid #ccc', padding: 1 }}>
              <img
                src={img.url}
                alt={`Preview ${idx}`}
                width={100}
                height={100}
                style={{ objectFit: 'cover' }}
              />
              <TextField
                size="small"
                label="Alt Text"
                value={img.alt_text ?? ''}
                onChange={e => {
                  const updated = [...productImages];
                  updated[idx].alt_text = e.target.value;
                  setProductImages(updated);
                }}
              />
              <Button
                size="small"
                color="error"
                onClick={() => {
                  setProductImages(prev => prev.filter((_, i) => i !== idx));
                }}
              >
                Remove
              </Button>
            </Box>
          ))}
        </Box>
      </Box>
      <div>
        {isConnected ? (
          <FormControlLabel
            control={<Checkbox defaultChecked />}
            label="Post to Instagram"
            onChange={() => handlePostToIg()}
          />
        ) : (
          <p>You can't post to Ig</p>
        )}

        {loading && postToIg && error}
      </div>
      {/* Submit */}
      <Button onClick={handleSubmit} variant="contained" color="primary">
        Add Stock
      </Button>
    </Box>
  );
};

export default CreateInventory;
